import { useAppDispatch, useAppSelector } from "@assessment/store/store";
import { useEffect, useRef, useState } from "react";

import { InfoComponent } from "@lxp-app/components/atoms/infoComponent";
import Button from "@lxp-app/components/atoms/button";
import GetHeroIcon from "@lxp-app/utils/getHeroIcon";
import {
  useGetAttemptsRemainingQuery,
  useGetSavedVideoMutation,
  useGetSaveSubjectiveQLQuery,
  useSaveSubjectiveMutation
} from "@assessment/store/slices/api/assessmentApiSlice";
import { useShowErrors } from "../../../toasterWrapper";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import Tooltip from "@lxp-app/components/atoms/tooltip";
import ContentDeleteModal from "@lxp-app/components/molecules/modal-children/contentDeleteModal";
import Modal from "@lxp-app/components/atoms/modal";
import VideoViewer from "@assessment/components/atoms/video/videoViewer";
import { useTranslation } from "react-i18next";

interface videoResponse {
  uploadConfig: any;
  question: any;
  sectionId: string;
}

const VideoResponse = ({
  uploadConfig,
  question,
  sectionId
}: videoResponse) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const maxDuration =
    (uploadConfig?.maxUploadDuration ? uploadConfig.maxUploadDuration : 2) * 60;
  const minDuration =
    (uploadConfig?.minUploadDuration ? uploadConfig.minUploadDuration : 0.5) *
    60;

  const constants = useAppSelector((store) => store.AssessmentSlice.duration);
  const practiceModeDuration = constants?.practiceDurationInSec;

  const minPercent = (minDuration / maxDuration) * 100;

  const showErrors = useShowErrors();

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // flag to track if component is unmounted
  const isUnmountedRef = useRef(false);

  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [videoURL, setVideoURL] = useState<string>("");
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string>("");
  const [recordingTime, setRecordingTime] = useState<number>(0);
  const [isPracticeMode, setIsPracticeMode] = useState<boolean>(false);
  const [fileSize, setFileSize] = useState<number>(0);
  const [extension, setExtension] = useState<string>("");
  const [url, setUrl] = useState<string>("");
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  // const [isSaving, setIsSaving] = useState(false);
  const [qnStatus, setQnStatus] = useState<string>("");
  const [isChanged, setIsChanged] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const recordingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const [saveSubjectiveApi] = useSaveSubjectiveMutation();
  const [getFile] = useGetSavedVideoMutation();

  const ongoingId = useAppSelector((store) => store.AssessmentSlice.ongoingId);
  const isSaving = useAppSelector((store) => store.AssessmentSlice.isSaving);
  const assessmentProgress = useAppSelector(
    (store) => store.AssessmentSlice.assessmentProgress
  );

  const { data, isLoading } = useGetSaveSubjectiveQLQuery(
    {
      ongoingId: ongoingId,
      questionId: question?.questionId,
      questionVersion: question?.versionnumber
    },
    {
      refetchOnMountOrArgChange: true
    }
  );
  const { data: attemptsLeft, refetch } = useGetAttemptsRemainingQuery(
    {
      ongoingId: ongoingId,
      questionId: question?.questionId!,
      quesVersion: question?.versionnumber!
    },
    {
      refetchOnMountOrArgChange: true
    }
  );

  const attemptsRemaining = attemptsLeft?.data;
  const remaining = uploadConfig?.uploadLimit - attemptsRemaining;

  const fileUrl: any =
    data?.getSaveSubjectiveQL?.data?.savedSubjective?.[0]?.fileDetails
      ?.filePath;

  useEffect(() => {
    if (assessmentProgress?.[sectionId]) {
      const qnList = assessmentProgress?.[sectionId]?.questions;
      if (question?.questionId in qnList) {
        if (qnList?.[question?.questionId]?.status)
          setQnStatus(qnList?.[question?.questionId]?.status);
      }
    }
  }, [assessmentProgress, question?.questionId]);

  useEffect(() => {
    if (fileUrl) getUploadedFile();
  }, [fileUrl]);

  useEffect(() => {
    isRecording && setIsRecording(false);
    isPracticeMode && setIsPracticeMode(false);
    clearVideoRef();
  }, [question?.questionId]);

  const getUploadedFile = async () => {
    try {
      const body = { url: fileUrl };
      const res: any = await getFile(body);
      const url = res?.data?.data?.url;
      setUrl(url);
    } catch (error: any) {
      console.log(error);
    }
  };

  // Format time
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  // stops any active stream and sets the unmount flag
  // so in-flight getUserMedia calls know not to keep the stream alive
  useEffect(() => {
    isUnmountedRef.current = false;

    return () => {
      isUnmountedRef.current = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const initCamera = async () => {
      try {
        // Skip if component already unmounted
        if (isUnmountedRef.current) return;

        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        });

        // If component unmounted while getUserMedia was in-flight,
        // stop the newly created stream immediately to prevent orphaned camera
        if (isUnmountedRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.muted = true;
          await videoRef.current.play();
        }
      } catch (err) {
        console.error("Camera error:", err);
      }
    };

    if (!url && !videoURL) {
      initCamera();
    } else {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [url, videoURL]);

  useEffect(() => {
    if (!videoRef.current) return;

    if (url) {
      // 🎥 Show saved video
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      videoRef.current.srcObject = null;
      videoRef.current.src = url;
      videoRef.current.muted = true;
      // videoRef.current.play();
    }
  }, [url]);

  // Start Recording
  const startRecording = async () => {
    clearVideoRef();
    setIsChanged(true);
    if (!streamRef.current) return;

    const mediaRecorder = new MediaRecorder(streamRef.current);
    mediaRecorderRef.current = mediaRecorder;

    const chunks: BlobPart[] = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    const startTime = Date.now();
    const tempDuration = isPracticeMode ? practiceModeDuration : maxDuration;
    recordingIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setRecordingTime(elapsed);
      if (elapsed > tempDuration) {
        stopRecording();
      }
    }, 250);

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunks, { type: "video/mp4" });
      const url = URL.createObjectURL(blob);
      const extension = blob.type.split("/")[1];
      setVideoBlob(blob);
      setFileSize(blob.size);
      setExtension(extension);

      // Create temp video to measure duration
      const tempVideo = document.createElement("video");
      tempVideo.src = url;

      tempVideo.onloadedmetadata = () => {
        const recordedDuration = tempVideo.duration;

        if (recordedDuration < minDuration) {
          setError(
            `Your video must be at least ${uploadConfig?.minUploadDuration} minutes long. The current recording is only ${Math.floor(
              recordedDuration
            )} seconds.`
          );
        }

        setVideoURL(url);
        dispatch(
          AssessmentSliceActions.mutateCandidateResponse({
            questionId: question?.questionId,
            answerKey: [url]
          })
        );

        if (videoRef.current) {
          videoRef.current.srcObject = null;
          videoRef.current.src = url;
          videoRef.current.muted = false;
        }
      };
    };

    mediaRecorder.start();
    setIsRecording(true);
  };

  // Stop Recording
  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
  };

  // Play / Pause
  const togglePlay = () => {
    if (!videoRef.current) return;

    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const clearVideoRef = () => {
    if (!isPracticeMode) setUrl("");
    setError("");
    setRecordingTime(0);
    setFileSize(0);
    setExtension("");
    setVideoBlob(null);
    setVideoURL("");
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    if (videoRef.current && streamRef.current) {
      videoRef.current.src = "";
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.muted = true;
      videoRef.current.play();
    }
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    dispatch(
      AssessmentSliceActions.mutateCandidateResponse({
        questionId: question?.questionId,
        answerKey: []
      })
    );
  };

  // Update progress
  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    setCurrentTime(videoRef.current.currentTime);
    setDuration(videoRef.current.duration || 0);
  };

  // Seek
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = percent * duration;
  };

  const startPractice = () => {
    setError("");
    // setVideoURL("");
    if (isPracticeMode && isRecording) {
      setIsRecording(false);
    }
    clearVideoRef();
    setIsPracticeMode(!isPracticeMode);
  };

  const handleModal = (): void => {
    setModalOpen(!modalOpen);
  };

  const submitResponse = async () => {
    try {
      dispatch(AssessmentSliceActions.mutateSaving(true));
      const res: any = await saveSubjectiveApi({
        ongoingId: ongoingId ?? "",
        questionId: question?.questionId,
        quesVersion: question?.versionnumber ?? 1,
        body: {
          solution: "",
          uploadConfigId: uploadConfig?.id,
          fileDetails: {
            fileName: "subjective",
            fileSize: fileSize,
            fileExtension: extension,
            filePath: "string"
          }
        }
      });
      if (res?.data?.data?.uploadUrlDetails?.data) {
        const uploadConfig = res?.data?.data?.uploadUrlDetails?.data;

        if (!videoBlob) {
          setError("No video file found");
          return;
        }
        const file = new File([videoBlob], "recorded-video.mp4", {
          type: "video/mp4"
        });

        uploadFileToSignedUrl(
          uploadConfig.upload_url,
          file,
          uploadConfig.submission_uuid
        );

        // }
      } else dispatch(AssessmentSliceActions.mutateSaving(false));
    } catch (error: any) {
      dispatch(AssessmentSliceActions.mutateSaving(false));
    }
  };
  const uploadFileToSignedUrl = async (
    signedUrl: string,
    file: File,
    submissionUuid: string
  ) => {
    try {
      const res = await fetch(signedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "video/mp4",
          "x-goog-meta-submission-uuid": submissionUuid
        },
        body: file
      });
      if (!res.ok) {
        if (showErrors)
          showErrors(
            "Upload Failed",
            [],
            "error",
            "File upload failed. Please try again later!"
          );
        // throw new Error("Upload failed");
      }
      dispatch(
        AssessmentSliceActions.mutateCandidateResponse({
          questionId: question?.questionId,
          answerKey: []
        })
      );
      refetch();
      if (showErrors)
        showErrors(
          "File Uploaded",
          [],
          "success",
          "Successfully uploaded the video file."
        );
      dispatch(AssessmentSliceActions.mutateSaving(false));
      dispatch(
        AssessmentSliceActions.mutateQuestionProgress({
          questionId: question.questionId,
          sectionId: sectionId,
          status: "completed"
        })
      );
      setIsChanged(false);
      return res;
    } catch (error: any) {
      dispatch(AssessmentSliceActions.mutateSaving(false));
      if (showErrors)
        showErrors(
          "Upload Failed",
          [],
          "error",
          "File upload failed. Please try again later!"
        );
    }
  };

  const getLabel = () => {
    if (isRecording) {
      if (isPracticeMode) return "Stop Practice";
      else return "Stop Recording";
    } else {
      if (isPracticeMode) return "Start Practice";
      else return "Start Recording";
    }
  };

  const attemptsRemainingMsg = () => {
    if (qnStatus == "completed" && remaining == 0) {
      return (
        <div className=" text-xs font-semibold text-red-600">
          No Attempts Left{" "}
        </div>
      );
    } else {
      if (remaining == 1)
        return (
          <div className=" text-xs font-semibold text-amber-800">
            Last Attempt
          </div>
        );
      else
        return (
          <div className=" text-xs font-semibold text-gray-800">
            Attempts Remaining:{" "}
            {qnStatus == "completed" ? remaining : uploadConfig?.uploadLimit}{" "}
          </div>
        );
    }
  };

  if (url) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-1 min-h-0">
          <div className="relative h-full bg-black rounded-xl overflow-hidden">
            <div className="h-full w-full">
              <VideoViewer
                options={{
                  sources: [{ src: url, type: "video/mp4" }],
                  autoplay: false,
                  controls: true,
                  responsive: true,
                  fill: true
                }}
                className="custom-player"
                videoSrc={url}
              />
            </div>
          </div>
        </div>
        {remaining == 0 && (
          <div
            className="absolute top-2 right-2 transform
                    opacity-100 px-3 py-1.5 rounded-full bg-white
                    transition duration-300 z-10"
          >
            <div className="flex gap-2 cursor-pointer ">
              {attemptsRemainingMsg()}
            </div>
          </div>
        )}

        {remaining > 0 && (
          <div className="mt-4 mb-4 flex justify-end">
            <Button
              variant="outlined"
              label={qnStatus !== "completed" ? "Retake" : "Reattempt"}
              onClick={clearVideoRef}
              iconName="ArrowPathIcon"
              buttonColor="secondary"
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto h-full flex flex-col">
      {/* Video Section */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="relative bg-black rounded-t-xl overflow-hidden group h-full">
          <video
            ref={videoRef}
            onTimeUpdate={handleTimeUpdate}
            onLoadStart={() => setIsVideoLoading(true)}
            onLoadedMetadata={() => {
              if (!videoRef.current) return;
              setCurrentTime(0);
              videoRef.current && setDuration(videoRef?.current.duration || 0);
              setIsVideoLoading(false);
            }}
            onEnded={() => setIsPlaying(false)}
            className="w-full h-full object-cover"
          />

          {(isVideoLoading || !videoRef?.current) && (
            <div className="absolute inset-0 flex items-center justify-center bg-black z-20">
              <div className="flex flex-col items-center gap-2">
                <div className="w-16 h-16 rounded-full bg-gray-700 flex items-center justify-center">
                  <GetHeroIcon
                    name="UserIcon"
                    className="w-8 h-8 text-gray-300"
                    variant="solid"
                  />
                </div>
              </div>
            </div>
          )}

          {!isPracticeMode && (
            <div
              className="absolute top-2 right-2 transform
                    opacity-100 px-3 py-1.5 rounded-full bg-white
                    transition duration-300 z-10"
            >
              <div className="flex gap-2 cursor-pointer ">
                {attemptsRemainingMsg()}
              </div>
            </div>
          )}

          {isRecording && !isPracticeMode && (
            <div
              className="absolute top-2 left-2 transform
                    opacity-100 px-3 py-1.5 rounded-full bg-black/25
                    transition duration-300 z-10"
            >
              <div className="flex gap-2 cursor-pointer ">
                <div className="relative flex h-2.5 w-2.5 mt-1">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-600 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600"></span>
                </div>
                <div className="text-white text-xs font-semibold">
                  RECORDING
                </div>
              </div>
            </div>
          )}
          {isPracticeMode && qnStatus != "completed" && (
            <div
              className="absolute top-2 left-2 transform
                    opacity-100 px-3 py-1.5 rounded-full bg-blue-200/30
                    transition duration-300 z-50"
            >
              <div
                onClick={clearVideoRef}
                className="flex gap-1 cursor-pointer"
              >
                <div className="bg-blue-800 w-3 h-3 mt-0.5 rounded-full" />
                <div className="text-blue-800 text-xs font-semibold">
                  PRACTICE MODE
                </div>
              </div>
            </div>
          )}

          {(videoURL || url) && !isRecording && (
            <div
              className={`absolute inset-0 flex flex-col items-center justify-center
                   group-hover:opacity-100 transition duration-300 z-10
                  pointer-events-none ${isPlaying ? "opacity-0" : "opacity-50"}`}
            >
              {/* Play/Pause Button */}
              <button
                onClick={togglePlay}
                className="pointer-events-auto bg-black/50 p-4 rounded-full flex items-center justify-center
                 hover:scale-110 transition-transform"
              >
                <GetHeroIcon
                  name={isPlaying ? "PauseIcon" : "PlayIcon"}
                  className="w-6 h-6 text-white"
                  aria-hidden="true"
                  variant="solid"
                />
              </button>
            </div>
          )}
          {/* Overlay Controls */}
          {(videoURL || url) && !isRecording && (
            <div
              className="absolute bottom-0 left-0 w-full 
                    bg-gradient-to-t from-black/80 to-transparent 
                    p-3 opacity-0 group-hover:opacity-100 
                    transition duration-300"
            >
              {/* Progress Bar */}
              <div
                onClick={handleSeek}
                className="w-full h-2 bg-gray-400/40 rounded cursor-pointer"
              >
                <div
                  className="h-2 bg-primary rounded"
                  style={{
                    width: duration
                      ? `${(currentTime / duration) * 100}%`
                      : "0%"
                  }}
                />
              </div>

              {/* Timestamp display */}
              <div className="flex justify-between items-center text-xs text-white mt-2">
                <div className="flex items-center gap-3">
                  <span>
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>
              </div>
            </div>
          )}
          {isRecording && (
            <div
              className="absolute bottom-0 left-0 w-full p-3 
                            bg-gradient-to-t from-black/80 to-transparent 
                            opacity-100 transition duration-300"
            >
              <div className="flex items-center gap-2">
                {/* Progress Bar */}
                <div className="relative flex-1 h-2 bg-gray-400/40 rounded">
                  {/* Current Progress */}
                  <div
                    className="h-2 bg-primary rounded transition-all duration-200"
                    style={{
                      width: `${isPracticeMode ? (recordingTime / practiceModeDuration) * 100 : (recordingTime / maxDuration) * 100}%`
                    }}
                  />

                  {/* Min Marker + Tooltip */}
                  {!isPracticeMode && (
                    <div
                      className="absolute top-1/2 -translate-y-1/2 group"
                      style={{ left: `${minPercent}%` }}
                    >
                      <div className="w-[2px] h-4 bg-white cursor-pointer" />

                      <div
                        className="absolute bottom-6 left-1/2 -translate-x-1/2 
                                         bg-black text-white text-[10px] px-2 py-1 
                                        rounded opacity-0 group-hover:opacity-100 
                                            transition whitespace-nowrap"
                      >
                        Min {formatTime(minDuration)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Timestamp */}
              <div className="flex justify-between items-center text-xs text-white mt-2">
                <span>
                  {formatTime(recordingTime)} /{" "}
                  {isPracticeMode
                    ? formatTime(practiceModeDuration)
                    : formatTime(maxDuration)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {isPracticeMode && (
        <div className="mt-2 px-2">
          <InfoComponent
            textSize={"xs"}
            iconSize={4}
            iconVariant={"outline"}
            text={
              "Recordings made here are not submitted for evaluation. Use this space to practice before starting the actual assessment."
            }
            iconPosition={"start"}
          />
        </div>
      )}
      {/* Error */}
      {error?.length > 0 && !isPracticeMode && (
        <div className="mt-2 px-2">
          <InfoComponent
            textSize={"xs"}
            iconSize={4}
            iconVariant={"outline"}
            text={error}
            iconPosition={"start"}
            messageType="warning"
          />
        </div>
      )}

      {/* Buttons */}
      {remaining > 0 && (
        <div className="pt-4 px-4">
          <div
            className={`flex pb-10 gap-2 
                ${qnStatus != "completed" ? "justify-between" : "justify-end"}`}
          >
            {qnStatus != "completed" && (
              <Button
                variant="outlined"
                label={isPracticeMode ? "Exit Practice Mode" : "Practice Mode"}
                onClick={startPractice}
                buttonColor="secondary"
              />
            )}

            <div className="flex items-center gap-4">
              {!videoURL && !url ? (
                <Button
                  variant="contained"
                  label={getLabel()}
                  onClick={isRecording ? stopRecording : startRecording}
                  iconName={isRecording ? "StopIcon" : "MicrophoneIcon"}
                  buttonColor={isRecording ? "warning" : "secondary"}
                />
              ) : (
                <Button
                  variant="outlined"
                  label={qnStatus != "completed" ? "Retake" : "Reattempt"}
                  onClick={clearVideoRef}
                  iconName="ArrowPathIcon"
                  buttonColor="secondary"
                />
              )}

              {!isPracticeMode &&
                videoURL &&
                error?.length == 0 &&
                isChanged && (
                  <Button
                    variant="contained"
                    label={
                      qnStatus == "completed"
                        ? `Reupload Response`
                        : `Submit Response`
                    }
                    onClick={() => {
                      if (!isSaving) handleModal();
                    }}
                    isLoading={isSaving}
                    loadingLabel={t("assessment.subjective.submittingResponse")}
                    buttonColor="primary"
                    loaderClass=""
                    loadingLabelClass=""
                  />
                )}
              {/* </Tooltip> */}
            </div>
          </div>
        </div>
      )}

      <Modal
        open={modalOpen}
        setOpen={handleModal}
        dialogueClassName="sm:max-w-3xl"
        children={
          <ContentDeleteModal
            isShow={modalOpen}
            show={() => {
              submitResponse();
              handleModal();
            }}
            setOpen={handleModal}
            buttonName={
              qnStatus == "completed" ? `Reupload Response` : `Submit Response`
            }
            showRemarksDeleteModal={false}
            heading="Submit Response"
            msg={`You have a total of ${remaining ? remaining : uploadConfig?.uploadLimit} response submissions remaining. Are you sure you want to submit this response?`}
          />
        }
      />
    </div>
  );
};

export default VideoResponse;
